import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import "../../../styles/adminList.css";

const initialsOf = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const AV_TINTS = [
  { bg: "var(--color-info-bg)", color: "var(--color-info-text)" },
  { bg: "var(--color-violet-bg)", color: "var(--color-violet-text)" },
  { bg: "var(--color-success-bg)", color: "var(--color-success-text)" },
  { bg: "var(--color-warning-bg)", color: "var(--color-warning-text)" },
];
const avTint = (i) => AV_TINTS[i % AV_TINTS.length];

const Category = () => {
  const Navigate = useNavigate();
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [data, setData] = useState([]);
  const [totalpage, setTotalPage] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [dense, setDense] = useState(false);
  const [sortDir, setSortDir] = useState("");

  // ✅ Fetch categories
  const CategoryData = () => {
    setLoading(true);
    axios
      .get(
        `${process.env.REACT_APP_URL}/admin/category?page=${page}&limit=10&search=${search}&filter=${monthFilter}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` } }
      )
      .then((res) => {
        setTotalPage(res?.data?.data?.pagination?.totalPages || 0);
        setData(res?.data?.data?.categories || []);
      })
      .catch((err) => {
        if (err.response?.data?.message === "Unauthorized!") {
          Navigate("/admin/login");
        }
      })
      .finally(() => setLoading(false));
  };

  // ✅ Toggle Active/Deactive
  const HandleActiveDeactive = (id, status) => {
    let statusText = status ? "Deactivate" : "Activate";
    let Color = status ? "#DC3741" : "#1b55e2";

    Swal.fire({
      title: `Do you want to ${statusText} this category?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: Color,
      confirmButtonText: statusText,
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .put(
            `${process.env.REACT_APP_URL}/admin/category/${id}`,
            { is_active: !status },
            { headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` } }
          )
          .then((res) => {
            toast.success(res.data.message);
            CategoryData();
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || "Failed to update");
          });
      }
    });
  };

  // ✅ Delete Category
  const HandleDelete = (id) => {
    Swal.fire({
      title: "Do you want to Delete this category?",
      icon: "error",
      showCancelButton: true,
      confirmButtonColor: "#DC3741",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`${process.env.REACT_APP_URL}/admin/category/${id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
          })
          .then((res) => {
            Swal.fire({
              title: "Deleted Successfully.",
              icon: "success",
              showConfirmButton: false,
              timer: 2000,
            });
            CategoryData();
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || "Failed to delete");
          });
      }
    });
  };

  // ✅ Pagination
  const handlePageClick = (event) => setPage(event.selected + 1);

  // ✅ Fetch on mount and dependency change
  useEffect(() => {
    CategoryData();
  }, [page, search, monthFilter]);

  const fmt = (d) =>
    d
      ? new Date(d).toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
      : "—";

  const displayData = useMemo(() => {
    const rows = [...(data || [])];
    if (sortDir) {
      rows.sort((a, b) => {
        const av = (a.product_category_name || "").toLowerCase();
        const bv = (b.product_category_name || "").toLowerCase();
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [data, sortDir]);

  const Toggle = ({ item }) => (
    <button
      type="button"
      className={`al-toggle ${item.is_active ? "is-on" : ""}`}
      title={item.is_active ? "Deactivate" : "Activate"}
      onClick={() => HandleActiveDeactive(item.product_category_id, item.is_active)}
    >
      <span className="al-toggle__knob" />
    </button>
  );

  const RowActions = ({ item }) => (
    <div className="al-actions">
      <button
        type="button"
        className="al-act al-act--primary"
        title="Edit"
        onClick={() =>
          Navigate("/admin/category/edit", { state: { id: item.product_category_id } })
        }
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
        </svg>
      </button>
      <button
        type="button"
        className="al-act al-act--danger"
        title="Delete"
        onClick={() => HandleDelete(item.product_category_id)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </button>
    </div>
  );

  const GRID = { gridTemplateColumns: "1.6fr 1.2fr .8fr 96px" };

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />

      {/* Page header */}
      <div className="al-head">
        <div>
          <h1 className="al-title">Categories</h1>
          <p className="al-sub">Top-level product categories.</p>
        </div>
        <div className="al-head__actions">
          <button
            type="button"
            className="al-btn al-btn--primary"
            onClick={() => Navigate("/admin/category/add")}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Category
          </button>
        </div>
      </div>

      {/* Table card */}
      <div className="al-card">
        <div className="al-toolbar">
          <div className="al-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Search categories…"
              onKeyDown={(e) => e.key === "Enter" && search !== "" && CategoryData()}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="al-select">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              <option value="">Select Month</option>
              <option value="All">All</option>
              <option value="1">Last 1 Month</option>
              <option value="3">Last 3 Months</option>
              <option value="6">Last 6 Months</option>
            </select>
          </div>
          <button
            type="button"
            className="al-tool"
            style={{ marginLeft: "auto" }}
            onClick={() => setDense((d) => !d)}
            title="Toggle density"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
            {dense ? "Comfortable" : "Density"}
          </button>
        </div>

        {!isMobileView ? (
          <div className="al-tablewrap">
            <div style={{ minWidth: 760, "--al-row-pad": dense ? "8px" : "13px" }}>
              <div className="al-thead" style={GRID}>
                <span className="al-th al-th--sort" onClick={() => setSortDir((d) => (d === "asc" ? "desc" : d === "desc" ? "" : "asc"))}>
                  Name
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: sortDir ? 1 : 0.45, transform: sortDir === "desc" ? "rotate(180deg)" : "none" }}>
                    <polyline points="18 15 12 9 6 15" />
                  </svg>
                </span>
                <span className="al-th">Date &amp; Time</span>
                <span className="al-th">Is Active</span>
                <span className="al-th al-th--right">Action</span>
              </div>

              {loading &&
                [...Array(5)].map((_, i) => (
                  <div className="al-row" style={GRID} key={`sk-${i}`}>
                    <div className="al-id">
                      <span className="al-skel" style={{ width: 32, height: 32, borderRadius: 9 }} />
                      <span className="al-skel" style={{ width: 120, height: 12 }} />
                    </div>
                    <span className="al-skel" style={{ width: 120, height: 12 }} />
                    <span className="al-skel" style={{ width: 46, height: 26, borderRadius: 14 }} />
                    <span className="al-skel" style={{ width: 64, height: 30, borderRadius: 8, marginLeft: "auto" }} />
                  </div>
                ))}

              {!loading &&
                displayData.map((item, index) => {
                  const t = avTint(index);
                  return (
                    <div className="al-row" style={GRID} key={item.product_category_id}>
                      <div className="al-id">
                        <span className="al-avatar" style={{ background: t.bg, color: t.color }}>
                          {initialsOf(item.product_category_name)}
                        </span>
                        <span className="al-name">{item.product_category_name}</span>
                      </div>
                      <span className="al-mono al-muted">{fmt(item.created_at)}</span>
                      <span><Toggle item={item} /></span>
                      <RowActions item={item} />
                    </div>
                  );
                })}

              {!loading && displayData.length === 0 && (
                <div className="al-empty">
                  <div className="al-empty__icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 2 7 12 12 22 7 12 2" />
                      <polyline points="2 17 12 22 22 17" />
                      <polyline points="2 12 12 17 22 12" />
                    </svg>
                  </div>
                  <p className="al-empty__title">No Categories Found</p>
                  <p className="al-empty__text">Try a different search or add a category.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="al-mobile">
            {!loading && displayData.length > 0 ? (
              displayData.map((item, index) => {
                const t = avTint(index);
                return (
                  <div className="al-mcard" key={item.product_category_id}>
                    <div className="al-mcard__top">
                      <span className="al-avatar" style={{ background: t.bg, color: t.color }}>
                        {initialsOf(item.product_category_name)}
                      </span>
                      <div className="al-name" style={{ flex: 1 }}>{item.product_category_name}</div>
                      <Toggle item={item} />
                    </div>
                    <div className="al-mrow">
                      <span className="al-muted">Date</span>
                      <span className="al-mono al-muted">{fmt(item.created_at)}</span>
                    </div>
                    <div className="al-mactions">
                      <RowActions item={item} />
                    </div>
                  </div>
                );
              })
            ) : (
              !loading && (
                <div className="al-empty">
                  <p className="al-empty__title">No Categories Found</p>
                </div>
              )
            )}
          </div>
        )}

        <div className="al-foot">
          <span className="al-foot__info">
            Showing <strong>{displayData.length}</strong> categories
          </span>
          {data.length !== 0 && (
            <ReactPaginate
              previousLabel={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              }
              nextLabel={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              }
              breakLabel={"..."}
              pageCount={totalpage}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"pagination justify-content-end m-0"}
              pageClassName={"page-item"}
              pageLinkClassName={"page-link"}
              previousClassName={"page-item"}
              previousLinkClassName={"page-link"}
              nextClassName={"page-item"}
              nextLinkClassName={"page-link"}
              breakClassName={"page-item"}
              breakLinkClassName={"page-link"}
              activeClassName={"active"}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Category;
